import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock, FiUser, FiDollarSign, FiMonitor, FiPlay, FiLoader, FiAlertCircle, FiSearch, FiFilter, FiShoppingCart } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchField from '../components/SearchField';
import { apiUrls, getHttpHeaders } from '../config/api';
import { cartService } from '../services/cartService';

// Utility function to strip HTML tags and clean up text
const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  
  // Remove HTML tags
  const withoutTags = html.replace(/<[^>]*>/g, ' ');
  
  // Replace HTML entities
  const withoutEntities = withoutTags
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Clean up extra whitespace and truncate for display
  const cleaned = withoutEntities
    .replace(/\s+/g, ' ')
    .trim();
  
  // Truncate for table display (first 150 characters)
  return cleaned.length > 150 ? cleaned.substring(0, 150) + '...' : cleaned;
};

interface OnlineClassesPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick: () => void;
  onMkAcademyInPersonClick: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

interface Course {
  id: number;
  title: string;
  mode: 'online' | 'in-person';
  instructor: string;
  description: string;
  price: number;
  level: string;
  image?: string;
  created_at: string;
  updated_at: string;
}

interface CourseDetails {
  id: number;
  course_id: number;
  syllabus: string;
  duration: string;
  prerequisites: string;
  features: string[];
  metadata: any;
}

interface Batch {
  id: number;
  batch_name: string;
  schedule: string;
  start_date: string;
  end_date: string;
  max_students: number;
  location?: string;
  address?: string;
  created_at: string;
  updated_at: string;
}

interface BatchCourse {
  id: number;
  batch_id: number;
  course_id: number;
  created_at: string;
}

interface BatchStudent {
  id: number;
  batch_id: number;
  student_id: number;
  enrollment_date: string;
  status: 'active' | 'completed' | 'dropped' | 'suspended';
  progress: number;
  grade?: string;
}

interface OnlineClassWithEnrollment {
  course_id: number;
  title: string;
  instructor: string;
  description: string;
  price: number;
  level: string;
  image?: string;
  duration: string;
  prerequisites: string;
  features: string[];
  batch_id: number;
  batch_name: string;
  schedule: string;
  start_date: string;
  end_date: string;
  max_students: number;
  enrolled_students: number;
  status: 'Available' | 'Limited' | 'Full' | 'Completed';
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: #253C8C;
  margin-bottom: 16px;
  font-weight: 700;
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: #718096;
  max-width: 600px;
  margin: 0 auto;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(37, 60, 140, 0.1);
  overflow: hidden;
  margin-bottom: 40px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  color: white;
`;

const TableHeaderRow = styled.tr`
  &:hover {
    background: rgba(37, 60, 140, 0.1);
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr<{ $isEven?: boolean }>`
  border-bottom: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  background: ${props => props.$isEven ? '#e0e7ff' : '#f8fafc'};
  
  &:hover {
    background: #ddd6fe;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px 20px;
  font-size: 0.95rem;
  vertical-align: top;
`;

const ClassTitle = styled.div`
  font-weight: 600;
  color: #253C8C;
  font-size: 1.1rem;
  margin-bottom: 4px;
`;

const ClassInstructor = styled.div`
  color: #253C8C;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
`;

const ClassDescription = styled.div`
  color: #718096;
  line-height: 1.5;
  max-width: 300px;
`;

const DetailInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #4a5568;
`;

const Price = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #253C8C;
  margin-bottom: 8px;
`;

const EnrollButton = styled.button`
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3);
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  ${props => {
    switch (props.status) {
      case 'Available':
        return 'background: #dcfce7; color: #166534;';
      case 'Limited':
        return 'background: #fef3c7; color: #92400e;';
      case 'Full':
        return 'background: #fee2e2; color: #dc2626;';
      case 'Completed':
        return 'background: #dbeafe; color: #1e40af;';
      default:
        return 'background: #f3f4f6; color: #374151;';
    }
  }}
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const LoadingSpinner = styled.div`
  animation: spin 1s linear infinite;
  color: #7c3aed;
  margin-bottom: 16px;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #7c3aed;
  font-size: 1.1rem;
  font-weight: 500;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: #fee2e2;
  border-radius: 12px;
  margin: 20px 0;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 12px;
`;

const RetryButton = styled.button`
  background: #7c3aed;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #6d28d9;
    transform: translateY(-2px);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #718096;
`;

const EmptyStateText = styled.p`
  font-size: 1.2rem;
  margin-bottom: 8px;
`;

const EmptyStateSubtext = styled.p`
  font-size: 1rem;
  color: #9ca3af;
`;

const OnlineClassesPage: React.FC<OnlineClassesPageProps> = ({
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onFaqClick,
  onFeedbackClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [classes, setClasses] = useState<OnlineClassWithEnrollment[]>([]);
  const [allClasses, setAllClasses] = useState<OnlineClassWithEnrollment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [addedToCart, setAddedToCart] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchOnlineClasses();
  }, []);

  // Filter classes based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setClasses(allClasses);
    } else {
      const filteredClasses = allClasses.filter(classItem => 
        classItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classItem.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        classItem.instructor.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setClasses(filteredClasses);
    }
  }, [searchTerm, allClasses]);

  const handleEnrollClick = (classItem: OnlineClassWithEnrollment) => {
    // Create cart item for the online class
    const cartItem = {
      id: classItem.course_id,
      title: classItem.title,
      price: classItem.price,
      image: classItem.image,
      type: 'course' as const
    };

    const success = cartService.addToCart(cartItem);
    
    if (success) {
      setAddedToCart(prev => new Set(prev).add(classItem.course_id));
      
      // Dispatch event to open cart
      window.dispatchEvent(new CustomEvent('openCart'));
      
      // Reset added state after 3 seconds
      setTimeout(() => {
        setAddedToCart(prev => {
          const newSet = new Set(prev);
          newSet.delete(classItem.course_id);
          return newSet;
        });
      }, 3000);
    } else {
      alert('Failed to add to cart. Please try again.');
    }
  };

  const fetchOnlineClasses = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      console.log('🔄 Fetching online classes with enrollment data...');

      const response = await fetch(apiUrls.academy.onlineClassesWithEnrollment, {
        headers: getHttpHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Online classes data received:', data);

      // Extract the classes array from the response
      const classesArray = data.classes || data || [];

      // Process the data to calculate status based on enrollment
      const processedClasses = classesArray.map((classItem: any) => {
        const enrollmentPercentage = (classItem.enrolled_students / classItem.max_students) * 100;
        let status: 'Available' | 'Limited' | 'Full' | 'Completed' = 'Available';
        
        // Determine status based on enrollment and dates
        const now = new Date();
        const endDate = new Date(classItem.end_date);
        
        if (endDate < now) {
          status = 'Completed';
        } else if (classItem.enrolled_students >= classItem.max_students) {
          status = 'Full';
        } else if (enrollmentPercentage >= 80) {
          status = 'Limited';
        } else {
          status = 'Available';
        }

        return {
          ...classItem,
          status,
          features: Array.isArray(classItem.features) ? classItem.features : 
                   (classItem.features ? classItem.features.split(',').map((f: string) => f.trim()) : [])
        };
      });

      setAllClasses(processedClasses);
      setClasses(processedClasses);
      console.log('📚 Processed online classes:', processedClasses);

    } catch (error) {
      console.error('❌ Error fetching online classes:', error);
      setError(`Failed to load online classes: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header
        onHomeClick={onHomeClick}
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onLoginClick={onLoginClick}
        onAdminClick={onAdminClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
        onMkAcademyInPersonClick={onMkAcademyInPersonClick}
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        onLogout={onLogout}
        currentPage="onlineClasses"
      />
      
      <PageContainer>
        <ContentContainer>
          <PageHeader>
            <PageTitle>💻 Online Classes</PageTitle>
            <PageSubtitle>
              Join our interactive online math classes from the comfort of your home. 
              Learn from expert instructors with live sessions and comprehensive materials.
            </PageSubtitle>
          </PageHeader>


          {loading ? (
            <LoadingContainer>
              <LoadingSpinner>
                <FiLoader size={32} />
              </LoadingSpinner>
              <LoadingText>Loading online classes...</LoadingText>
            </LoadingContainer>
          ) : error ? (
            <ErrorContainer>
              <FiAlertCircle size={32} color="#dc2626" />
              <ErrorText>{error}</ErrorText>
              <RetryButton onClick={fetchOnlineClasses}>
                Try Again
              </RetryButton>
            </ErrorContainer>
          ) : classes.length === 0 ? (
            <EmptyState>
              <EmptyStateText>No online classes available</EmptyStateText>
              <EmptyStateSubtext>Check back later for new course offerings</EmptyStateSubtext>
            </EmptyState>
          ) : (
            <>
              {/* Integrated Search Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                borderRadius: '12px',
                padding: '16px 24px',
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 15px rgba(124, 58, 237, 0.15)'
              }}>
                <div>
                  <h2 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'white',
                    margin: 0
                  }}>
                    💻 Online Classes
                  </h2>
                  <p style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    margin: '4px 0 0 0',
                    fontWeight: '500'
                  }}>
                    {classes.length} classes available
                  </p>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {/* Search Field in Banner */}
                  <div style={{ minWidth: '320px' }}>
                    <SearchField
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder="Search courses, instructors..."
                      isLoading={loading}
                    />
                  </div>
                </div>
              </div>

              <TableContainer>
                <Table>
                  <TableHeader>
                    <TableHeaderRow>
                      <TableHeaderCell>Course Details</TableHeaderCell>
                      <TableHeaderCell>Instructor</TableHeaderCell>
                      <TableHeaderCell>Schedule & Duration</TableHeaderCell>
                      <TableHeaderCell>Price & Action</TableHeaderCell>
                    </TableHeaderRow>
                  </TableHeader>
                <TableBody>
                  {classes.map((classItem, index) => (
                    <TableRow key={`${classItem.course_id}-${classItem.batch_id}`} $isEven={index % 2 === 0}>
                      <TableCell>
                        <ClassTitle>{classItem.title}</ClassTitle>
                        <ClassDescription>{stripHtmlTags(classItem.description)}</ClassDescription>
                        <DetailItem style={{ marginTop: '8px' }}>
                          <FiUser size={14} />
                          {classItem.level} Level
                        </DetailItem>
                        {classItem.prerequisites && (
                          <DetailItem style={{ marginTop: '4px', fontStyle: 'italic' }}>
                            Prerequisites: {classItem.prerequisites}
                          </DetailItem>
                        )}
                      </TableCell>
                      
                      <TableCell>
                        <ClassInstructor>
                          <FiUser size={16} />
                          {classItem.instructor}
                        </ClassInstructor>
                      </TableCell>
                      
                      <TableCell>
                        <DetailInfo>
                          <DetailItem>
                            <FiCalendar size={14} />
                            {classItem.schedule}
                          </DetailItem>
                          <DetailItem>
                            <FiClock size={14} />
                            {classItem.duration}
                          </DetailItem>
                          <DetailItem style={{ color: '#7c3aed', fontWeight: '500' }}>
                            Starts: {new Date(classItem.start_date).toLocaleDateString()}
                          </DetailItem>
                          <DetailItem style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                            Ends: {new Date(classItem.end_date).toLocaleDateString()}
                          </DetailItem>
                        </DetailInfo>
                      </TableCell>
                      
                      <TableCell>
                        <Price>${classItem.price}</Price>
                        <EnrollButton 
                          disabled={classItem.status === 'Full' || classItem.status === 'Completed'}
                          onClick={() => handleEnrollClick(classItem)}
                          style={{
                            background: addedToCart.has(classItem.course_id) 
                              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                              : 'linear-gradient(135deg, #253C8C 0%, #1e40af 100%)'
                          }}
                        >
                          {addedToCart.has(classItem.course_id) ? (
                            <>
                              <FiShoppingCart size={14} style={{ marginRight: '4px' }} />
                              ✓ Added to Cart
                            </>
                          ) : classItem.status === 'Full' ? 'Class Full' : 
                           classItem.status === 'Completed' ? 'Completed' : (
                            <>
                              <FiShoppingCart size={14} style={{ marginRight: '4px' }} />
                              Enroll Now
                            </>
                          )}
                        </EnrollButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </ContentContainer>
      </PageContainer>
      <Footer 
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
      onFaqClick={onFaqClick}
      onFeedbackClick={onFeedbackClick}
      onMkAcademyOnlineClick={onMkAcademyOnlineClick}
      />
    </>
  );
};

export default OnlineClassesPage;